// Shared helper for every backend call.
//
// The backend answers with `{ error: "..." }` and HTTP 200 (and sometimes with a
// `status` JSON property that only looks like an HTTP code), so `response.ok` is
// never enough on its own - `data.error` counts as a failure too.
// A 500 from Elysia comes back as text/plain, so the body is always read as text
// and only then parsed as JSON: `response.json()` would throw a confusing
// SyntaxError that hides the real error.
//
// Only network failures (offline, DNS, CORS) reject. Every HTTP status resolves.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXCERPT_LENGTH = 300;

// Short, log-friendly slice of a response body.
export const bodyExcerpt = (rawBody) => {
    if (!rawBody) {
        return "";
    }

    const trimmed = String(rawBody).trim().replace(/\s+/g, " ");
    return trimmed.length > EXCERPT_LENGTH ? `${trimmed.slice(0, EXCERPT_LENGTH)}...` : trimmed;
};

export const callBackend = async (path, { token, method = "GET", body } = {}) => {
    const headers = {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${BACKEND_URL}${path}`, {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    const rawBody = await response.text();

    let data = null;
    let parseError = null;
    if (rawBody) {
        try {
            data = JSON.parse(rawBody);
        } catch (err) {
            parseError = err;
        }
    }

    // The header is the primary source; the body copy is the fallback. Both may be absent.
    const requestId = response.headers.get("x-request-id") || (data && data.requestId) || null;

    const backendError = data && data.error ? data.error : null;
    const ok = response.ok && !backendError && !parseError;

    let error = null;
    if (backendError) {
        error = backendError;
    } else if (!response.ok) {
        // The status comes before the parse error on purpose: an expired Clerk token
        // answers `error(401, "Unauthorized")` with the bare string `Unauthorized`, and
        // an ALB 502/503 answers with HTML - both fail JSON.parse. "HTTP 401
        // Unauthorized" is the useful headline there, not the parser's complaint.
        error = `HTTP ${response.status} ${response.statusText}`.trim();
    } else if (parseError) {
        error = `Malformed response body (${parseError.message})`;
    }

    if (!ok) {
        console.error("Backend call failed", {
            path,
            httpStatus: response.status,
            requestId,
            error,
            rawBody: bodyExcerpt(rawBody),
        });
    }

    return { ok, httpStatus: response.status, data, rawBody, requestId, error };
};

// Human-readable one-liner for a failed callBackend result: what broke, on which
// request, and enough of the body to recognise it. `rid` is what you grep for in
// the pod logs.
export const formatBackendError = (result, prefix) => {
    const message = result.error || "Request failed";
    const head = prefix ? `${prefix}: ${message}` : message;
    const detail = `HTTP ${result.httpStatus}, rid=${result.requestId || "unavailable"}`;
    const excerpt = bodyExcerpt(result.rawBody);

    if (excerpt && excerpt !== message) {
        return `${head} [${detail}] - ${excerpt}`;
    }

    return `${head} [${detail}]`;
};

// Same shape of message for the only case that rejects: no response came back. Whether
// the backend ever saw the request is unknowable from here - `/update-status` runs for
// minutes and usually keeps going after the connection drops, so this must not claim
// the request was never sent.
export const formatNetworkError = (err, prefix) => {
    const message = `Network error - ${err && err.message ? err.message : String(err)} (no response received - the request may still be running; check the pod logs before retrying)`;
    return prefix ? `${prefix}: ${message}` : message;
};

export const backendUrl = BACKEND_URL;
